import FileSaver from 'file-saver';

const HEIC_EXTENSIONS = ['heic', 'heif'];
const DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'xlsm', 'xlsx', 'plain', 'csv', 'doc'];
const DISPLAYABLE_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

const heicPreviewCache = new Map();

export function getFileExtension(url) {
  if (!url) {
    return '';
  }

  try {
    const pathname = new URL(url).pathname;
    return pathname.split('.').pop().toLowerCase();
  } catch (e) {
    const filename = url.split('?')[0].split('/').pop() || '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  }
}

export function isDisplayableImageUrl(url) {
  return DISPLAYABLE_IMAGE_EXTENSIONS.includes(getFileExtension(url));
}

export function mightNeedHeicConversion(url) {
  const ext = getFileExtension(url);
  return HEIC_EXTENSIONS.includes(ext) || ext === 'undefined';
}

export function isHeicFile(url) {
  return mightNeedHeicConversion(url);
}

export function isDocumentFile(url) {
  return DOCUMENT_EXTENSIONS.includes(getFileExtension(url));
}

export async function downloadFile(fileUrl) {
  const fileName = decodeURIComponent(
    fileUrl.substring(fileUrl.lastIndexOf('/') + 1).split('?')[0],
  );

  try {
    const response = await fetch(fileUrl);

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    FileSaver.saveAs(blob, fileName);
  } catch (error) {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

async function convertHeicBlob(blob) {
  const { isHeic, heicTo } = await import('heic-to');
  const blobIsHeic = await isHeic(blob);

  if (!blobIsHeic) {
    return null;
  }

  const heicBlob = blob.type && blob.type !== 'application/octet-stream'
    ? blob
    : new Blob([await blob.arrayBuffer()], { type: 'image/heic' });

  return heicTo({
    blob: heicBlob,
    type: 'image/jpeg',
    quality: 0.9,
  });
}

export async function getDisplayableImageSrc(url) {
  if (!url) {
    return url;
  }

  if (heicPreviewCache.has(url)) {
    return heicPreviewCache.get(url);
  }

  // Standard images work in <img> tags without fetch; presigned S3 URLs often block fetch (CORS).
  if (isDisplayableImageUrl(url)) {
    return url;
  }

  if (!mightNeedHeicConversion(url)) {
    return url;
  }

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();
    const converted = await convertHeicBlob(blob);

    if (!converted) {
      return url;
    }

    const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
    const objectUrl = URL.createObjectURL(jpegBlob);

    heicPreviewCache.set(url, objectUrl);
    return objectUrl;
  } catch (error) {
    console.warn('HEIC preview unavailable (CORS or unsupported format):', url, error);
    return url;
  }
}
